import {
  type ElementSelection,
  type HexEditPreviewMessage,
  HexVersionId,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";

import { DataAppChromeColors } from "../../util/dataAppChromeTheme.js";
import { RootState } from "../store";

export type DataAppViewMode = "preview" | "code" | "select" | "edit";

/**
 * Chrome colors currently being applied to host UI (AppNavbar) next to the
 * data app. Populated from the runtime-sampled value postMessaged by the
 * iframe after render.
 */
type DataAppThemeState = DataAppChromeColors;

/** Where to put the cursor in the source panel, in offsets into the file. */
interface DataAppSourceReveal {
  path: string;
  start: number;
  end: number;
  nonce: number;
}

interface SelectModeEntry {
  seq: number;
  fromButton: boolean;
}

export interface StateUpdateCounts {
  seq: number;
  externalSeq: number;
}

export const NO_STATE_UPDATES: StateUpdateCounts = { seq: 0, externalSeq: 0 };

/** How many recent update ids to keep for deduping the live subscribers. */
const RECENT_STATE_UPDATE_ID_LIMIT = 16;

export interface DataAppVersionState {
  /**
   * Snapshot era from the last `dataAppState` publish applied here. Used to
   * gate edit-panel preview writes and invalidation — file contents live in
   * `dataAppFileMPSlice`.
   */
  lastModifiedAt: number | null;
  /**
   * Live state updates so far (`dataAppStateUpdated` events): every update,
   * and the ones not caused by this tab's own chart-edit writes. The panel
   * compares both against what it last saw, so an external update is never
   * missed even when an own one lands in the same render. Only the live
   * subscription records these; query results are snapshots, not events.
   */
  lastStateUpdate: StateUpdateCounts | null;
  /**
   * Ids of recent state updates, so one event delivered to several live
   * subscribers on the same version counts once.
   */
  recentStateUpdateIds: string[];
  /**
   * `lastModifiedAt` of the newest snapshot a recovery refetch reported as an
   * external update. A live update at or before it describes state that
   * refetch already reported, and is not counted again.
   */
  stateUpdatesReportedThrough: number | null;
  /** Which file is open in the source panel of the App Builder tab. */
  selectedFile: string | null;
  /**
   * A one-shot request to put the cursor somewhere in the open file. The
   * editor consumes it once and clears it, so the same span can be asked for
   * again -- `nonce` is what makes a second click of the same element move a
   * cursor the user has since moved away.
   */
  sourceReveal: DataAppSourceReveal | null;
  /** Whether the App Builder tab shows the live preview or the source panel. */
  viewMode: DataAppViewMode;
  /**
   * The most recent switch from preview into select or edit mode: whether the
   * top-bar Select button made it, and a running count so one entry can be
   * told from the next. Null until the first entry; kept after leaving so the
   * count keeps climbing.
   */
  lastSelectModeEntry: SelectModeEntry | null;
  appTheme: DataAppThemeState | null;
  /** The chart element selected in edit mode (null = nothing selected). */
  elementSelection: ElementSelection | null;
  /**
   * Count of `setElementSelection` dispatches stamped `isUserInteraction`
   * (see that payload's doc). Consumers treat each bump as a one-shot
   * scroll/focus request. Clears never bump it, so "revision unchanged" does
   * NOT mean the selection still exists — check that separately.
   */
  selectionRevision: number;
  /**
   * What is under the pointer in select/edit mode: a seeded component type
   * ("LineChart") or a text element's kind ("Heading"). Null when nothing
   * selectable is hovered.
   */
  elementHoverComponent: string | null;
  /**
   * Frame-reported qualifier for it, in display form: a chart's region
   * ("Legend") or a preview of a text element's words.
   */
  elementHoverRegion: string | null;
  /** Ephemeral edit-panel preview bound for the iframe (never persisted). */
  editPreview: EditPreviewState | null;
}

/**
 * `values: null` means "clear the preview for `target`" — the shape mirrors
 * the HEX_EDIT_PREVIEW message, and a clear must still name its target.
 * `lastModifiedAt` is the files era the preview was computed against; the
 * reducer drops writes from a different era (see setEditPreview).
 */
interface EditPreviewState {
  target: HexEditPreviewMessage["target"];
  values: Record<string, unknown> | null;
  lastModifiedAt: number | null;
}

type DataAppSliceState = Record<HexVersionId, DataAppVersionState | undefined>;

const initialVersionState = (): DataAppVersionState => ({
  lastModifiedAt: null,
  lastStateUpdate: null,
  recentStateUpdateIds: [],
  stateUpdatesReportedThrough: null,
  selectedFile: null,
  sourceReveal: null,
  viewMode: "preview",
  lastSelectModeEntry: null,
  appTheme: null,
  elementSelection: null,
  selectionRevision: 0,
  elementHoverComponent: null,
  elementHoverRegion: null,
  editPreview: null,
});

const dataAppSlice = createSlice({
  name: "dataApp",
  initialState: {} as DataAppSliceState,
  reducers: {
    setDataAppState: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        lastModifiedAt: number;
      }>,
    ) => {
      const { hexVersionId, lastModifiedAt } = action.payload;
      const existing = state[hexVersionId];
      if (
        existing?.lastModifiedAt != null &&
        lastModifiedAt <= existing.lastModifiedAt
      ) {
        return;
      }
      const version = existing ?? initialVersionState();
      state[hexVersionId] = {
        ...version,
        lastModifiedAt,
        // A preview is meaningless against files it wasn't made for. Clearing
        // here (not in an effect) is what keeps the post-republish re-embed
        // from replaying a stale preview into the fresh iframe. The
        // selection deliberately survives (the panel must outlive Apply →
        // republish), but its embedded currentValues are a snapshot from
        // before the file change — panel consumers must re-derive current
        // values rather than trust them after this.
        editPreview: null,
      };
    },
    stateUpdateReceived: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        updateId: string;
        own: boolean;
        lastModifiedAt: number;
      }>,
    ) => {
      const { hexVersionId, lastModifiedAt, own, updateId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      if (existing.recentStateUpdateIds.includes(updateId)) return;
      if (
        existing.stateUpdatesReportedThrough != null &&
        lastModifiedAt <= existing.stateUpdatesReportedThrough
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        lastStateUpdate: countStateUpdate(existing.lastStateUpdate, own),
        recentStateUpdateIds: [
          ...existing.recentStateUpdateIds,
          updateId,
        ].slice(-RECENT_STATE_UPDATE_ID_LIMIT),
      };
    },
    /**
     * A recovery refetch brought files newer than the store had: an update
     * was missed while the socket was down, and nothing says whose, so it
     * counts as external.
     */
    stateSnapshotRefetched: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        lastModifiedAt: number;
      }>,
    ) => {
      const { hexVersionId, lastModifiedAt } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = {
        ...existing,
        lastStateUpdate: countStateUpdate(existing.lastStateUpdate, false),
        stateUpdatesReportedThrough: lastModifiedAt,
      };
    },
    setDataAppTheme: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        theme: DataAppThemeState | null;
      }>,
    ) => {
      const { hexVersionId, theme } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, appTheme: theme };
    },
    setDataAppNotFound: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      state[hexVersionId] = initialVersionState();
    },
    setDataAppViewMode: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        viewMode: DataAppViewMode;
        /** True when the top-bar Select button itself made the change. */
        fromButton?: boolean;
      }>,
    ) => {
      const { fromButton = false, hexVersionId, viewMode } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const inSelectionMode = viewMode === "select" || viewMode === "edit";
      const wasInSelectionMode =
        existing.viewMode === "select" || existing.viewMode === "edit";
      const entering = inSelectionMode && !wasInSelectionMode;
      state[hexVersionId] = {
        ...existing,
        viewMode,
        lastSelectModeEntry: entering
          ? { seq: (existing.lastSelectModeEntry?.seq ?? 0) + 1, fromButton }
          : existing.lastSelectModeEntry,
        elementSelection: inSelectionMode ? existing.elementSelection : null,
        elementHoverComponent: inSelectionMode
          ? existing.elementHoverComponent
          : null,
        elementHoverRegion: inSelectionMode
          ? existing.elementHoverRegion
          : null,
      };
    },
    /**
     * Backs the selection flow out one level: a selection (locked-in or
     * provisional) → select mode with nothing selected, empty select mode →
     * preview. Shared by the edit panel Close button, host Escape, and
     * Escape relayed from the iframe.
     */
    dismissElementSelection: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      if (existing == null) return;
      if (
        (existing.viewMode === "edit" || existing.viewMode === "select") &&
        existing.elementSelection != null
      ) {
        state[hexVersionId] = {
          ...existing,
          viewMode: "select",
          elementSelection: null,
        };
        return;
      }
      if (existing.viewMode === "select" || existing.viewMode === "edit") {
        state[hexVersionId] = {
          ...existing,
          viewMode: "preview",
          elementSelection: null,
          elementHoverComponent: null,
          elementHoverRegion: null,
        };
      }
    },
    setElementSelection: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        selection: ElementSelection | null;
        /**
         * Set only where the dispatch is a genuine fresh user interaction
         * (element click, chart-menu edit request) — never on the maintenance
         * dispatches (apply fold, re-embed reconcile, clears). Bumps
         * `selectionRevision`.
         */
        isUserInteraction?: true;
      }>,
    ) => {
      const { hexVersionId, isUserInteraction, selection } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const stamped = isUserInteraction === true && selection != null;
      const nextRevision = stamped
        ? existing.selectionRevision + 1
        : existing.selectionRevision;
      // Content-identical means identity, geometry, and reported values all
      // match. mountId, currentValues, and codeDefinedKeys must participate:
      // Apply folds edits into currentValues only, the post-republish
      // reconcile changes mountId only, and provenance may change
      // independently of both.
      const previous = existing.elementSelection;
      const unchanged =
        selection != null &&
        previous != null &&
        previous.kind === selection.kind &&
        previous.rect.x === selection.rect.x &&
        previous.rect.y === selection.rect.y &&
        previous.rect.width === selection.rect.width &&
        previous.rect.height === selection.rect.height &&
        (selection.kind === "text"
          ? previous.kind === "text" &&
            previous.tagId === selection.tagId &&
            previous.elementType === selection.elementType &&
            previous.label === selection.label
          : previous.kind === "chart" &&
            previous.component === selection.component &&
            previous.mountId === selection.mountId &&
            previous.instanceId === selection.instanceId &&
            previous.region === selection.region &&
            previous.seriesKey === selection.seriesKey &&
            isEqual(previous.currentValues, selection.currentValues) &&
            isEqual(previous.codeDefinedKeys, selection.codeDefinedKeys));
      // An unstamped content-identical dispatch is a no-op, so an app looping
      // selection posts can't churn every subscriber with fresh objects. A
      // stamped one can legitimately re-select the already-selected element
      // (e.g. reopening "Edit style" on the same chart): keep the object
      // reference but still advance the revision so the panel scrolls/focuses
      // again.
      if (unchanged && !stamped) return;
      state[hexVersionId] = {
        ...existing,
        elementSelection: unchanged ? previous : selection,
        selectionRevision: nextRevision,
      };
    },
    /**
     * Refreshes the selected chart's reported values in place, when the frame
     * re-reports them because the chart's props moved under the live
     * selection. Deliberately not `setElementSelection`: the selection has not
     * changed, and re-setting it would read downstream as a fresh click.
     */
    setElementSelectionValues: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        currentValues: Record<string, unknown>;
      }>,
    ) => {
      const { currentValues, hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      const selection = existing?.elementSelection;
      if (existing == null || selection == null || selection.kind !== "chart") {
        return;
      }
      // Charts re-report on every prop change, most of which don't touch a
      // reported value; an unchanged object must not re-render the panel.
      if (
        JSON.stringify(selection.currentValues) ===
        JSON.stringify(currentValues)
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        elementSelection: { ...selection, currentValues },
      };
    },
    setElementHoverComponent: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        component: string | null;
        region?: string | null;
      }>,
    ) => {
      const { component, hexVersionId, region = null } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      if (
        existing.elementHoverComponent === component &&
        existing.elementHoverRegion === region
      ) {
        return;
      }
      state[hexVersionId] = {
        ...existing,
        elementHoverComponent: component,
        elementHoverRegion: region,
      };
    },
    setEditPreview: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        preview: EditPreviewState | null;
      }>,
    ) => {
      const { hexVersionId, preview } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      // A panel debounce timer can fire in the gap between setDataAppState
      // (which cleared editPreview for the new files) and the panel's own
      // invalidation effect. Dropping writes stamped with a different files
      // era keeps that late write from replaying into the fresh iframe.
      if (
        preview != null &&
        preview.lastModifiedAt !== existing.lastModifiedAt
      ) {
        return;
      }
      state[hexVersionId] = { ...existing, editPreview: preview };
    },
    setDataAppSelectedFile: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        selectedFile: string | null;
      }>,
    ) => {
      const { hexVersionId, selectedFile } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, selectedFile };
    },
    requestDataAppSourceReveal: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        path: string;
        start: number;
        end: number;
      }>,
    ) => {
      const { end, hexVersionId, path, start } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = {
        ...existing,
        sourceReveal: {
          path,
          start,
          end,
          nonce: (existing.sourceReveal?.nonce ?? 0) + 1,
        },
      };
    },
    clearDataAppSourceReveal: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId; nonce: number }>,
    ) => {
      const { hexVersionId, nonce } = action.payload;
      const existing = state[hexVersionId];
      if (existing?.sourceReveal?.nonce !== nonce) {
        return;
      }
      state[hexVersionId] = { ...existing, sourceReveal: null };
    },
  },
});

export const {
  clearDataAppSourceReveal,
  dismissElementSelection,
  requestDataAppSourceReveal,
  setDataAppNotFound,
  setDataAppSelectedFile,
  setDataAppState,
  setDataAppTheme,
  setDataAppViewMode,
  setEditPreview,
  setElementHoverComponent,
  setElementSelection,
  setElementSelectionValues,
  stateSnapshotRefetched,
  stateUpdateReceived,
} = dataAppSlice.actions;

function countStateUpdate(
  previous: StateUpdateCounts | null,
  own: boolean,
): StateUpdateCounts {
  const counts = previous ?? NO_STATE_UPDATES;
  return {
    seq: counts.seq + 1,
    externalSeq: own ? counts.externalSeq : counts.externalSeq + 1,
  };
}

export const dataAppReducer = dataAppSlice.reducer;

const selectVersionState = (
  state: RootState,
  hexVersionId: HexVersionId,
): DataAppVersionState | undefined => state.dataApp[hexVersionId];

export const dataAppSelectors = {
  selectLastModifiedAt: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.lastModifiedAt ?? null,

  selectLastStateUpdate: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.lastStateUpdate ?? null,

  selectAppTheme: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.appTheme ?? null,

  selectSelectedFile: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.selectedFile ?? null,

  selectSourceReveal: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.sourceReveal ?? null,

  selectViewMode: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): DataAppViewMode =>
    selectVersionState(state, hexVersionId)?.viewMode ?? "preview",

  selectElementSelection: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): ElementSelection | null =>
    selectVersionState(state, hexVersionId)?.elementSelection ?? null,

  selectLastSelectModeEntry: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): SelectModeEntry | null =>
    selectVersionState(state, hexVersionId)?.lastSelectModeEntry ?? null,

  selectSelectionRevision: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): number => selectVersionState(state, hexVersionId)?.selectionRevision ?? 0,

  selectElementHoverComponent: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.elementHoverComponent ?? null,

  selectElementHoverRegion: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.elementHoverRegion ?? null,

  selectEditPreview: (
    state: RootState,
    hexVersionId: HexVersionId,
  ): EditPreviewState | null =>
    selectVersionState(state, hexVersionId)?.editPreview ?? null,
};
